/**
 * Consent-gated analytics scaffold.
 *
 * Nothing runs until the visitor accepts the cookie banner. Once `accepted`,
 * we lazy-init whatever analytics providers are configured via env variables:
 *   - REACT_APP_GA_MEASUREMENT_ID  (Google Analytics 4)
 *   - REACT_APP_PLAUSIBLE_DOMAIN   (Plausible - privacy-friendly alternative)
 *
 * If neither env var is set, we simply record page views to a debug console
 * log — safe no-op for local dev. Zero PII collection until consent.
 */

const CONSENT_KEY = "wofy_cookie_consent";
let _initialized = false;
let _consentGiven = false;

function loadGA4(measurementId) {
  if (window.gtag || !measurementId) return;
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(s);
  window.dataLayer = window.dataLayer || [];
  window.gtag = function () { window.dataLayer.push(arguments); };
  window.gtag("js", new Date());
  window.gtag("config", measurementId, { anonymize_ip: true, send_page_view: true });
  // eslint-disable-next-line no-console
  console.info("[analytics] GA4 initialized:", measurementId);
}

function loadPlausible(domain) {
  if (!domain) return;
  if (document.querySelector('script[data-plausible="true"]')) return;
  const s = document.createElement("script");
  s.defer = true;
  s.src = "https://plausible.io/js/script.js";
  s.setAttribute("data-domain", domain);
  s.setAttribute("data-plausible", "true");
  document.head.appendChild(s);
  // eslint-disable-next-line no-console
  console.info("[analytics] Plausible initialized:", domain);
}

function initProviders() {
  if (_initialized) return;
  const ga = process.env.REACT_APP_GA_MEASUREMENT_ID;
  const plausible = process.env.REACT_APP_PLAUSIBLE_DOMAIN;
  if (ga) loadGA4(ga);
  if (plausible) loadPlausible(plausible);
  if (!ga && !plausible) {
    // eslint-disable-next-line no-console
    console.info("[analytics] Consent granted — no provider configured (dev/no-op).");
  }
  _initialized = true;
}

/** Public: install listeners exactly once, from App.js at boot. */
export function installAnalyticsConsentHook() {
  // Replay a previous decision saved to localStorage.
  try {
    const prev = localStorage.getItem(CONSENT_KEY);
    if (prev === "accepted") {
      _consentGiven = true;
      initProviders();
    }
  } catch { /* localStorage blocked */ }

  // Listen for future consent changes.
  window.addEventListener("wofy-cookie-consent", (e) => {
    const choice = e?.detail?.choice;
    if (choice === "accepted") {
      _consentGiven = true;
      initProviders();
    }
    // On "rejected" we intentionally do nothing — providers remain uninitialized.
  });
}

/** Public: safe to call from any component; drops the event when no consent. */
export function track(event, props = {}) {
  if (!_consentGiven) return;
  try {
    if (window.gtag) window.gtag("event", event, props);
    if (window.plausible) window.plausible(event, { props });
  } catch { /* silent */ }
  // Also fire into SocialPulse Intelligence (best-effort, non-blocking).
  trackIntel(event, props);
}

// ─── SocialPulse Intelligence™ event stream ────────────────────────────
//
// Batches events client-side (1s window / 20-event flush), attaches the
// current session_id + site_source + page context, and POSTs to
// `/api/intelligence/events`. Silent on failure — analytics must never
// break the UX.
let _intelBuffer = [];
let _intelTimer = null;
let _sessionId = null;

function _sessId() {
  if (_sessionId) return _sessionId;
  try {
    _sessionId = sessionStorage.getItem("sp_intel_sid");
    if (!_sessionId) {
      _sessionId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem("sp_intel_sid", _sessionId);
    }
  } catch { _sessionId = `${Date.now().toString(36)}`; }
  return _sessionId;
}

function _flushIntel() {
  const batch = _intelBuffer;
  _intelBuffer = [];
  _intelTimer = null;
  if (!batch.length) return;
  const url = `${process.env.REACT_APP_BACKEND_URL}/api/intelligence/events`;
  const site = (() => {
    try {
      const q = new URLSearchParams(window.location.search).get("site");
      if (q) return q.toLowerCase();
      const host = window.location.hostname.toLowerCase();
      if (host.includes("foodpulse")) return "foodpulse";
      if (host.includes("indianfamilytree")) return "indianfamilytree";
      if (host.includes("travelerpulse")) return "travelerpulse";
      if (host.includes("naturepulse")) return "naturepulse";
      if (host.includes("keralahealth")) return "keralahealth";
      if (host.includes("alumnipulse")) return "alumnipulse";
      if (host.includes("indiaalumni")) return "indiaalumni";
      if (host.includes("familytree.team")) return "familytreeteam";
      return "socialpulse";
    } catch { return "socialpulse"; }
  })();
  // Use sendBeacon on page-hide, else fetch keepalive.
  const payload = JSON.stringify({ events: batch });
  try {
    if (navigator.sendBeacon && document.visibilityState === "hidden") {
      const blob = new Blob([payload], { type: "application/json" });
      navigator.sendBeacon(`${url}?site=${site}`, blob);
      return;
    }
  } catch { /* fall through */ }
  fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json", "X-Site-Source": site },
    body: payload,
    keepalive: true,
    credentials: "omit",
  }).catch(() => { /* silent */ });
}

export function trackIntel(event, props = {}) {
  if (!_consentGiven) return;
  try {
    _intelBuffer.push({
      event,
      session_id: _sessId(),
      page_path: typeof window !== "undefined" ? window.location.pathname : null,
      referrer: typeof document !== "undefined" ? document.referrer || null : null,
      consent_status: "granted",
      client_ts: new Date().toISOString(),
      metadata: props || {},
    });
    if (_intelBuffer.length >= 20) {
      if (_intelTimer) { clearTimeout(_intelTimer); _intelTimer = null; }
      _flushIntel();
    } else if (!_intelTimer) {
      _intelTimer = setTimeout(_flushIntel, 1000);
    }
  } catch { /* silent */ }
}

// Flush on page hide so we don't lose the trailing event on nav.
if (typeof window !== "undefined") {
  window.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") _flushIntel();
  });
  window.addEventListener("pagehide", _flushIntel);
}
